import type { Insets } from '../types'

import {
  BOTTOM_LIFT_VAR,
  CONTENT_SAFE_AREA_TOP_VAR,
  KEYBOARD_INSET_VAR,
  SAFE_AREA_BOTTOM_VAR,
  SAFE_AREA_TOP_VAR,
  SHEET_LIFT_VAR,
  SHEET_ROOM_VAR,
  VIEWPORT_BOTTOM_INSET_VAR,
  VIEWPORT_OFFSET_TOP_VAR,
  VIEWPORT_STABLE_HEIGHT_VAR,
} from './config'

export type ViewportMetrics = {
  /** Высота окна вебвью (`documentElement.clientHeight`). */
  layoutHeight: number
  /** Мёртвая полоса окна — уже без вклада клавиатуры (`createBottomLift`). */
  band: number
  /** Уже устоявшийся и ограниченный подъём (`createBottomLift`). */
  lift: number
  /** Где стоит нижняя кромка шторки (`createBottomLift`, правило 4). */
  sheetLift: number
  /** Видимое место над этой кромкой (`createBottomLift`, правило 5). */
  sheetRoom: number
  /** Сдвиг видимой полосы вниз внутри окна; 0, если клавиатуры нет. */
  offsetTop: number
  safeArea: Insets
  contentSafeArea: Insets
}

export type ViewportVars = Record<string, string>

/**
 * Safe area приходит из двух независимых источников, и ни одному нельзя доверять целиком:
 * Telegram отдаёт `safeAreaInset` только с Bot API 8.0, а `env(safe-area-inset-*)` внутри
 * вебвью Telegram на iOS долго был нулевым. Берём максимум прямо в CSS — кто из двоих знает
 * про чёлку, тот и выигрывает.
 */
function safeAreaValue(cssName: string, platformValue: number): string {
  return `max(env(${cssName}, 0px), ${Math.round(platformValue)}px)`
}

/**
 * Считает значения CSS-переменных вьюпорта. Чистая функция — вся возня с событиями и DOM
 * снаружи, в `ViewportRuntime`; вся возня с демпфированием — в `bottomLift.ts`.
 *
 * Здесь три разные величины, и путать их нельзя:
 *
 * 1. `layoutHeight` — окно вебвью. От него живёт `position: fixed`.
 * 2. `--tg-viewport-stable-height` — ВИДИМАЯ полоса окна. По ней верстается экран.
 * 3. `--tg-viewport-bottom-inset` — разница между ними: полоса окна ниже видимой части.
 *
 * Почему высоту экрана нельзя брать из одного `clientHeight`: в вебвью Telegram на iOS окно
 * выкладывается больше видимой полосы (Telegram-iOS#1296, Telegram-Mini-Apps/issues#39).
 * Экран верстался выше, чем виден, контент «влезал» в раскладку — переполнения не возникало,
 * и страница не скроллилась, а её низ просто оказывался за краем экрана. Ровно так пропал
 * скролл на главной, совместимости, статистике и в профиле, и ровно поэтому он уцелел на
 * длинном разборе натальной карты: там контент переполнял даже завышенную высоту.
 *
 * `--layout-bottom-lift` публикуется отсюда же одним числом. Собирать его в CSS из
 * `max(--tg-viewport-bottom-inset, --tg-keyboard-inset)` было ошибкой: демпфер стоял на одном
 * слагаемом, а второе приезжало сырым — см. `bottomLift.ts` и docs/telegram.md §5.3.
 *
 * Коробка шторки приезжает своей парой (`--layout-sheet-lift` / `--layout-sheet-room`) и тоже
 * готовыми числами. Шторке нужен не подъём футера, а нижняя граница ВИДИМОЙ полосы: пока она
 * ждала устоявшегося подъёма, полоса успевала уехать вверх, и шторка оставалась ниже всего,
 * что видно (docs/telegram.md §5.4). Считает обе величины `bottomLift.ts` — там же, где подъём,
 * и на тех же часах.
 */
export function computeViewportVars(metrics: ViewportMetrics): ViewportVars {
  const layoutHeight = Math.round(metrics.layoutHeight)
  const band = Math.round(metrics.band)
  const lift = Math.round(metrics.lift)

  const stableHeight = layoutHeight - band
  // Подъём больше мёртвой полосы бывает ровно по одной причине — клавиатуре.
  const keyboardInset = lift > band ? lift : 0

  return {
    // Коробка шторки — пара, и меняется парой: одно движение, один переход.
    [SHEET_LIFT_VAR]: `${Math.round(metrics.sheetLift)}px`,
    [SHEET_ROOM_VAR]: `${Math.round(metrics.sheetRoom)}px`,
    [VIEWPORT_STABLE_HEIGHT_VAR]: `${stableHeight}px`,
    [VIEWPORT_BOTTOM_INSET_VAR]: `${band}px`,
    [VIEWPORT_OFFSET_TOP_VAR]: `${Math.round(metrics.offsetTop)}px`,
    [KEYBOARD_INSET_VAR]: `${keyboardInset}px`,
    [BOTTOM_LIFT_VAR]: `${lift}px`,
    [SAFE_AREA_TOP_VAR]: safeAreaValue('safe-area-inset-top', metrics.safeArea.top),
    [SAFE_AREA_BOTTOM_VAR]: safeAreaValue('safe-area-inset-bottom', metrics.safeArea.bottom),
    [CONTENT_SAFE_AREA_TOP_VAR]: `${Math.round(metrics.contentSafeArea.top)}px`,
  }
}
